import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, ViewChild, signal } from '@angular/core';
import { MaterialModule } from '../../material.module';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist';

@Component({
  selector: 'app-jk-pdf-preview',
  standalone: true,
  imports: [MaterialModule],
  templateUrl: './jk-pdf-preview.component.html',
  styleUrl: './jk-pdf-preview.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JkPdfPreviewComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas') private canvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('stage') private stage?: ElementRef<HTMLElement>;
  readonly pageNumber = signal(1);
  readonly pageCount = signal(0);
  readonly scale = signal(1);
  readonly loading = signal(false);
  readonly error = signal('');
  private source: ArrayBuffer | null = null;
  private loadingTask?: PDFDocumentLoadingTask;
  private document?: PDFDocumentProxy;
  private renderTask?: RenderTask;
  private generation = 0;

  @Input({ required: true })
  set data(value: ArrayBuffer | null) {
    this.source = value;
    if (this.canvas) void this.load();
  }

  ngAfterViewInit(): void { void this.load(); }
  ngOnDestroy(): void { this.generation += 1; this.renderTask?.cancel(); void this.loadingTask?.destroy(); }

  previous(): void {
    if (this.pageNumber() > 1) { this.pageNumber.update(value => value - 1); void this.render(); }
  }

  next(): void {
    if (this.pageNumber() < this.pageCount()) { this.pageNumber.update(value => value + 1); void this.render(); }
  }

  zoom(delta: number): void {
    this.scale.update(value => Math.min(4, Math.max(0.25, value + delta)));
    void this.render();
  }

  async fitWidth(): Promise<void> {
    const page = await this.getPage();
    const width = this.stage?.nativeElement.clientWidth ?? 0;
    if (!page || !width) return;
    const viewport = page.getViewport({ scale: 1 });
    this.scale.set(Math.max(0.25, (width - 32) / viewport.width));
    await this.render(page);
  }

  private async load(): Promise<void> {
    const generation = ++this.generation;
    this.renderTask?.cancel();
    await this.loadingTask?.destroy();
    this.loadingTask = undefined;
    this.document = undefined;
    this.pageCount.set(0);
    this.pageNumber.set(1);
    this.error.set('');
    if (!this.source) return;
    this.loading.set(true);
    try {
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs/pdf.worker.min.mjs', document.baseURI).toString();
      const task = pdfjs.getDocument({ data: new Uint8Array(this.source.slice(0)) });
      this.loadingTask = task;
      const pdfDocument = await task.promise;
      if (generation !== this.generation) { await task.destroy(); return; }
      this.document = pdfDocument;
      this.pageCount.set(pdfDocument.numPages);
      await this.fitWidth();
    } catch {
      if (generation === this.generation) this.error.set('無法解析 PDF 檔案');
    } finally {
      if (generation === this.generation) this.loading.set(false);
    }
  }

  private async getPage(): Promise<PDFPageProxy | undefined> { return this.document?.getPage(this.pageNumber()); }

  private async render(existingPage?: PDFPageProxy): Promise<void> {
    const canvas = this.canvas?.nativeElement;
    const page = existingPage ?? await this.getPage();
    if (!canvas || !page) return;
    this.renderTask?.cancel();
    const viewport = page.getViewport({ scale: this.scale() });
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.floor(viewport.width * ratio);
    canvas.height = Math.floor(viewport.height * ratio);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    const context = canvas.getContext('2d');
    if (!context) return;
    this.renderTask = page.render({ canvas, canvasContext: context, viewport, transform: ratio === 1 ? undefined : [ratio, 0, 0, ratio, 0, 0] });
    try {
      await this.renderTask.promise;
    } catch (error) {
      if (!(error instanceof Error) || error.name !== 'RenderingCancelledException') throw error;
    }
  }
}
